import { BuilderPageType, ExposureConversationState, MethodologyTypeEnum } from "@/util/enum";
import { cloneDeep, isNull, isUndefined } from "lodash";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Drawer, FormModel, Input, Select, Checkbox, Button, Modal } from "ant-design-vue";
import { CommonLocale, ContentExposureLocale, ConversationBuilderLocale } from "@/locales/localeid";
import Spinning from "@/components/spinning/spinning.vue";
import {
	Action,
	ActionItem,
	ConversationPublishStatus,
	fmtMsg,
	GLUtil,
	MessageHelper,
	NotificationType
} from "commonui";
import {
	ConversationTemplateModel,
	ConversationTemplateService,
	MasterConversationType
} from "@/services/conversation-templates";
import { Paths } from "@/util/paths";
import {
	ConversationBuilderService,
	ExposureConversationDetailModel,
	RemoveExposureTemplateAssociationModel,
	SaveExposureConversationDetailModel
} from "@/services/conversation-builder";
import { getModule } from "vuex-module-decorators";
import { ConversationBuilderState } from "@/modules/conversation-builder";
import { disabledDueToAccessLevel, getRouteAccessLevel, showErrorMessage } from "@/util/func";

export interface ContentExposureConversationProps {
	disabled?: boolean;
	exposure?: any;
	refreshRecords?: () => Promise<void>;
}

const exposureFormDefaultValues: ExposureConversationDetailModel = {
	exposureId: "",
	openDialogId: null,
	conversationId: null,
	closeDialogId: null,
	isComplete: false,
	deleteCustomConversation: false,
	isCustomConversation: false
};

@Component({
	components: {
		Drawer,
		Spinning,
		Form: FormModel,
		FormItem: FormModel.Item,
		TextArea: Input.TextArea,
		Select,
		SelectOption: Select.Option,
		Checkbox,
		Button,
		Action,
		ActionItem
	}
})
export default class ContentExposureConversation extends Vue {
	@Prop({ default: false }) disabled!: boolean;
	@Prop({ default: null }) exposure!: any;
	@Prop() refreshRecords?: () => Promise<void>;
	@Prop({ default: -1 }) methodology!: number;

	private conversationStore = getModule(ConversationBuilderState);
	private conversationMasterDetails: ExposureConversationDetailModel | null = null;
	private templateService = ConversationTemplateService();
	public readonly notSetOptionId = null;
	public isDrawerVisible = false;
	public drawerTitle = "";
	public loading = false;
	public openTemplates: ConversationTemplateModel[] = [];
	public closeTemplates: ConversationTemplateModel[] = [];
	public readonly labelCol = {
		span: 7
	};
	public readonly wrapperCol = {
		span: 17
	};
	public exposureConversationForm = exposureFormDefaultValues;

	public exposureConversationFormRules = {
		openDialogId: [{ validator: this.openDialogValidator, trigger: "change" }],
		closeDialogId: [{ validator: this.closeDialogValidator, trigger: "change" }]
	};

	@Watch("conversationStore.currentPublishStatus", { immediate: true })
	showMessage() {
		const resetState = () => {
			this.conversationStore.setTrainingStatus(ConversationPublishStatus.Initial);
		};
		const { currentPublishStatus } = this.conversationStore;
		if (
			currentPublishStatus === ConversationPublishStatus.Initial ||
			currentPublishStatus === ConversationPublishStatus.Created ||
			currentPublishStatus === ConversationPublishStatus.Trained
		) {
			this.$message.info(fmtMsg(ConversationBuilderLocale.ConversationPublishingMsg));
			resetState();
		} else if (currentPublishStatus === ConversationPublishStatus.Deployed) {
			this.$message.info(fmtMsg(ConversationBuilderLocale.ConversationPublishedMsg));
			resetState();
		} else if (currentPublishStatus === ConversationPublishStatus.Failed) {
			this.$message.warning(fmtMsg(ConversationBuilderLocale.ConversationPublishFailedMsg));
			resetState();
		}
	}

	// validator function for open dialog
	openDialogValidator(rule: any, value: string, callback: (msg?: string) => void) {
		if (value === this.notSetOptionId) {
			if (!this.exposureConversationForm.isCustomConversation) {
				callback(
					this.formatMessage(
						this.contentExposureLocale.ContentExposureConversationOpenDialogValidationMessage
					)
				);
				return;
			} else if (this.exposureConversationForm.closeDialogId !== this.notSetOptionId) {
				callback(
					this.formatMessage(
						this.contentExposureLocale.ContentExposureConversationOpenDialogValidationMessage
					)
				);
				return;
			} else {
				this.resetValidation();
			}
		}

		callback();
	}

	// validator function for close dialog
	closeDialogValidator(rule: any, value: string, callback: (msg?: string) => void) {
		if (value === this.notSetOptionId) {
			if (!this.exposureConversationForm.isCustomConversation) {
				callback(
					this.formatMessage(
						this.contentExposureLocale.ContentExposureConversationCloseDialogValidationMessage
					)
				);
				return;
			} else if (this.exposureConversationForm.openDialogId !== this.notSetOptionId) {
				callback(
					this.formatMessage(
						this.contentExposureLocale.ContentExposureConversationCloseDialogValidationMessage
					)
				);
				return;
			} else {
				this.resetValidation();
			}
		}

		callback();
	}

	resetValidation() {
		const formRef = this.$refs.exposureConversationForm as FormModel;
		if (!isUndefined(formRef) && !isUndefined(formRef.clearValidate)) {
			formRef.clearValidate([]);
		}
	}

	resetFields() {
		this.exposureConversationForm = cloneDeep(exposureFormDefaultValues);
	}

	get isConversationIconVisible() {
		return !isNull(this.exposure) && this.methodology === MethodologyTypeEnum.Independent;
	}

	get contentExposureLocale() {
		return ContentExposureLocale;
	}

	get isDeleteDisabled() {
		const closeDialogId = this.conversationMasterDetails?.closeDialogId;
		const openDialogId = this.conversationMasterDetails?.openDialogId;
		// delete only when no conversation exists
		return !this.isConversationExist() && !closeDialogId && !openDialogId;
	}

	get disableSaveButton() {
		return (
			this.exposureConversationForm.openDialogId === this.notSetOptionId ||
			this.exposureConversationForm.closeDialogId === this.notSetOptionId
		);
	}

	get disableCompleteButton() {
		let isCompleteDisabled = this.disableSaveButton;

		if (isCompleteDisabled) {
			return isCompleteDisabled;
		}

		if (
			this.conversationMasterDetails?.isComplete &&
			this.conversationMasterDetails.openDialogId === this.exposureConversationForm.openDialogId &&
			this.conversationMasterDetails.closeDialogId === this.exposureConversationForm.closeDialogId
		) {
			isCompleteDisabled = true;
		}

		return isCompleteDisabled;
	}

	get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}

	get disabledDueToAccessLevel() {
		return disabledDueToAccessLevel(this.currentRouteAccessLevel);
	}

	deleteConversation() {
		if (this.isDeleteDisabled) {
			return;
		}
		const onOk = async () => {
			try {
				// delete the conversation
				await ConversationBuilderService.deleteConversation({
					exposureId: this.exposure.id
				});
				this.refreshRecords && (await this.refreshRecords());
				this.resetFields();
				this.resetValidation();
				this.conversationMasterDetails = null;
				this.exposureConversationForm.isCustomConversation = false;
				this.exposureConversationForm.openDialogId = this.notSetOptionId;
				this.exposureConversationForm.closeDialogId = this.notSetOptionId;
				MessageHelper.Message(
					NotificationType.Success,
					fmtMsg(this.contentExposureLocale.ContentExposureDeleteSuc)
				);
			} catch ({ error_code, error_description }) {
				showErrorMessage(
					{ error_code, error_description },
					fmtMsg(CommonLocale.CommonContentConversationAssociationDeleteMessage)
				);
			}
		};

		Modal.confirm({
			okText: fmtMsg(this.contentExposureLocale.ContentExposureDelete),
			title: fmtMsg(this.contentExposureLocale.ContentExposureDeleteWarn),
			onOk
		});
	}

	formatMessage(id: string, params?: any): string {
		return fmtMsg(id, params);
	}

	async navigateToMasterBuilder() {
		if (!isNull(this.exposure) && this.exposure.id && this.exposure.contentId) {
			const navigationUrl = GLUtil.pathStringify(Paths.ExposureConversationBuilder, {
				contentId: this.exposure.contentId,
				pagetype: BuilderPageType.ExposureInfo,
				id: this.exposure.id
			});

			if (this.conversationMasterDetails && !this.conversationMasterDetails.isCustomConversation) {
				await this.removeTemplateAssociation(navigationUrl);
			} else {
				this.$router.push(navigationUrl);
			}
		}
	}

	get getCustomConversationText() {
		return this.isConversationExist() && this.exposureConversationForm.isCustomConversation
			? this.formatMessage(this.contentExposureLocale.ContentExposureConversationEditCustomFlowText)
			: this.formatMessage(this.contentExposureLocale.ContentExposureConversationAddCustomFlowText);
	}

	setLoading(value: boolean) {
		this.loading = value;
	}

	get isCustomLinkDisabled() {
		// if (
		// 	this.exposureConversationForm.openDialogId !== this.notSetOptionId ||
		// 	this.exposureConversationForm.closeDialogId !== this.notSetOptionId
		// ) {
		// 	return true;
		// } else {
		// 	return false;
		// }

		return false;
	}

	get iconClass() {
		if (isNull(this.exposure) || this.exposure.conversationState === ExposureConversationState.DoesNotExist) {
			return "exposure-grey";
		} else if (this.exposure.conversationState === ExposureConversationState.Complete) {
			return "exposure-green";
		} else {
			return "exposure-red";
		}
	}

	createEditExposureConversation() {
		this.getOpenCloseTemplates();
		this.drawerTitle = this.isConversationExist()
			? fmtMsg(this.contentExposureLocale.ContentExposureConversationEditTitle)
			: fmtMsg(this.contentExposureLocale.ContentExposureConversationAddTitle);
		this.isDrawerVisible = true;
		if (this.isConversationExist()) {
			this.getConversationDetails();
		} else {
			this.resetFields();
			this.resetValidation();
		}
	}

	isConversationExist() {
		return (
			!isNull(this.exposure) &&
			(this.exposure.conversationState === ExposureConversationState.InComplete ||
				this.exposure.conversationState === ExposureConversationState.Complete)
		);
	}

	setFields() {}

	saveUpdateConversation(isComplete: boolean) {
		const formRef = this.$refs.exposureConversationForm as FormModel;
		formRef.validate((valid) => {
			if (valid) {
				const conversationDetails: SaveExposureConversationDetailModel = {
					exposureId: this.exposure.id,
					conversationId: this.exposure.conversationId,
					openDialogId: this.exposureConversationForm.openDialogId,
					closeDialogId: this.exposureConversationForm.closeDialogId,
					isComplete
				};

				this.saveUpdateConversationDetails(conversationDetails);
			}
		});
	}

	getConversationDetails() {
		if (this.isConversationExist()) {
			this.loading = true;
			ConversationBuilderService.getExposureConversationDetail(this.exposure.id)
				.then((details) => {
					this.exposureConversationForm = cloneDeep(details);
					this.conversationMasterDetails = cloneDeep(details);
					this.resetValidation();
					this.loading = false;
				})
				.catch(() => {
					this.loading = false;
				});
		}
	}

	saveUpdateConversationDetails(conversationDetails: SaveExposureConversationDetailModel) {
		this.loading = true;
		ConversationBuilderService.saveExposureConversation(conversationDetails)
			.then((response) => {
				this.refreshRecords && this.refreshRecords();
				this.loading = false;
				if (response && response.conversationId && response.publishDetails) {
					const { luisAccountId, luisAppId, appVersion } = response.publishDetails;
					this.conversationStore.setConversationExtraInfo({
						...this.conversationStore.setConversationExtraInfo,
						luisAccountId,
						luisAppId,
						appVersion
					});
					this.conversationStore.getTrainingStatus();
				}
				this.onDrawerClose();
			})
			.catch(() => {
				this.loading = false;
				// MessageHelper.Message(
				// 	NotificationType.Failed,
				// 	fmtMsg(this.contentExposureLocale.ContentExposureConversationSaveError)
				// );
			});
	}

	async removeTemplateAssociation(navigationUrl: string) {
		const removeDetails: RemoveExposureTemplateAssociationModel = {
			openDialogId: this.conversationMasterDetails?.openDialogId || 0,
			closeDialogId: this.conversationMasterDetails?.closeDialogId || 0,
			exposureId: this.exposure.id
		};
		this.loading = true;
		try {
			await ConversationBuilderService.removeExposureTemplateAssociation(removeDetails as any);
			this.loading = false;
			navigationUrl && this.$router.push(navigationUrl);
		} catch (e) {
			this.loading = false;
		}
	}

	onDrawerClose() {
		this.isDrawerVisible = false;
		this.openTemplates = [];
		this.closeTemplates = [];
	}

	getOpenCloseTemplates() {
		this.loading = true;

		this.templateService
			.getTemplates({
				types: [MasterConversationType.Open, MasterConversationType.Close]
			})
			.then((templates) => {
				this.openTemplates = templates.filter((t) => t.type === MasterConversationType.Open);
				this.closeTemplates = templates.filter((t) => t.type === MasterConversationType.Close);
				this.loading = false;
			})
			.catch(() => {
				this.loading = false;
			});
	}
}
